import { useEffect, useState, useCallback } from "react";
import { Loader2, LogOut, Lock, Inbox, Flame, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { DedicationsPanel } from "@/components/DedicationsPanel";
import { FemaleHomePanel } from "@/components/FemaleHomePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ["new", "contacted", "in_review", "admitted", "closed"];

const statusStyle = {
  new: "bg-purple-100 text-purple-700",
  contacted: "bg-blue-100 text-blue-700",
  in_review: "bg-amber-100 text-amber-700",
  admitted: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-200 text-slate-600",
};

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || "").join(" ");
  return String(detail);
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("jhoh_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [referrals, setReferrals] = useState(null);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("referrals");
  const [pendingCount, setPendingCount] = useState(0);

  const logout = useCallback(() => {
    localStorage.removeItem("jhoh_admin_token");
    setToken("");
  }, []);

  const loadPendingCount = useCallback(async (t) => {
    try {
      const res = await fetch(`${API}/admin/dedications?status=pending`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.ok) setPendingCount((await res.json()).length);
    } catch { /* non-critical badge */ }
  }, []);

  const load = useCallback(async (t, status) => {
    try {
      const url = status ? `${API}/admin/referrals?status=${status}` : `${API}/admin/referrals`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) {
        localStorage.removeItem("jhoh_admin_token");
        setToken("");
        return;
      }
      setReferrals(await res.json());
    } catch {
      toast.error("Could not load submissions");
    }
  }, []);

  useEffect(() => {
    if (token) { load(token, filter); loadPendingCount(token); }
  }, [token, filter, load, loadPendingCount, tab]);

  const login = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatError(data.detail));
      localStorage.setItem("jhoh_admin_token", data.token);
      setToken(data.token);
      toast.success(`Welcome back, ${data.user.name}`);
    } catch (err) {
      toast.error("Login failed", { description: err.message });
    } finally {
      setAuthLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${API}/admin/referrals/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      setReferrals((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="min-h-[70vh] flex items-center justify-center px-5 py-20 bg-[#FAF8F5] grain relative">
        <Reveal className="w-full max-w-md">
          <form onSubmit={login} data-testid="admin-login-form" className="bg-white rounded-3xl border border-purple-100 shadow-xl p-8 sm:p-10">
            <div className="w-14 h-14 rounded-2xl bg-[#F3E8FF] flex items-center justify-center mb-6">
              <Lock size={24} className="text-[#5B21B6]" />
            </div>
            <h1 className="font-display font-bold text-2xl text-[#2E1065]">Staff Login</h1>
            <p className="text-sm text-slate-500 mt-1 mb-7">Access referral submissions and Dedication Wall approvals.</p>
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Email</label>
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-purple-200 px-4 py-3 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#5B21B6]" placeholder="admin@jaliyahshouseofhopecom.org" />
            <label className="block text-xs font-bold uppercase tracking-wider text-[#2E1065] mb-2">Password</label>
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-purple-200 px-4 py-3 text-sm mb-6 focus:outline-none focus:ring-2 focus:ring-[#5B21B6]" placeholder="••••••••" />
            <button type="submit" disabled={authLoading} data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#5B21B6] hover:bg-[#2E1065] disabled:opacity-60 text-white font-bold px-8 py-3.5 rounded-full transition-colors duration-300">
              {authLoading && <Loader2 size={18} className="animate-spin" />}
              Sign In
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  const visible = (referrals || []).filter((r) => {
    const q = search.toLowerCase();
    return !q || r.full_name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.phone.includes(q);
  });

  return (
    <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#2E1065]" data-testid="admin-heading">Staff Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            {tab === "referrals" ? `${visible.length} referral${visible.length === 1 ? "" : "s"} shown` : tab === "female_home" ? "Publish the Female Home location and opening date" : "Review candles and messages before they go public"}
          </p>
        </div>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="inline-flex items-center gap-2 border-2 border-[#5B21B6] text-[#5B21B6] hover:bg-[#5B21B6] hover:text-white font-bold px-5 py-2.5 rounded-full text-sm transition-colors"
        >
          <LogOut size={16} /> Log out
        </button>
      </div>

      <div className="flex gap-2 mb-8 border-b border-purple-100" role="tablist">
        <button type="button" role="tab" data-testid="admin-tab-referrals" onClick={() => setTab("referrals")}
          className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "referrals" ? "border-[#5B21B6] text-[#5B21B6]" : "border-transparent text-slate-500 hover:text-[#5B21B6]"}`}>
          <Inbox size={16} /> Referrals
        </button>
        <button type="button" role="tab" data-testid="admin-tab-dedications" onClick={() => setTab("dedications")}
          className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "dedications" ? "border-[#5B21B6] text-[#5B21B6]" : "border-transparent text-slate-500 hover:text-[#5B21B6]"}`}>
          <Flame size={16} /> Dedication Wall
          {pendingCount > 0 && (
            <span data-testid="pending-dedications-badge" className="ml-1 bg-amber-400 text-[#2E1065] text-[11px] font-black px-2 py-0.5 rounded-full">{pendingCount}</span>
          )}
        </button>
        <button type="button" role="tab" data-testid="admin-tab-female-home" onClick={() => setTab("female_home")}
          className={`inline-flex items-center gap-2 px-5 py-3 text-sm font-bold border-b-2 -mb-px transition-colors ${tab === "female_home" ? "border-[#5B21B6] text-[#5B21B6]" : "border-transparent text-slate-500 hover:text-[#5B21B6]"}`}>
          <Megaphone size={16} /> Female Home
        </button>
      </div>

      {tab === "female_home" ? (
        <FemaleHomePanel token={token} onUnauthorized={logout} />
      ) : tab === "dedications" ? (
        <DedicationsPanel token={token} onUnauthorized={logout} />
      ) : (
      <>
      <div className="flex flex-wrap gap-3 mb-6">
        <input
          data-testid="admin-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, or phone..."
          className="rounded-full border border-purple-200 bg-white px-5 py-2.5 text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#5B21B6]"
        />
        <select
          data-testid="admin-filter-status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-full border border-purple-200 bg-white px-5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#5B21B6]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
      </div>

      {referrals === null ? (
        <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-[#5B21B6]" /></div>
      ) : visible.length === 0 ? (
        <div data-testid="admin-empty-state" className="text-center py-20 bg-white rounded-3xl border border-purple-100">
          <Inbox size={40} className="mx-auto text-purple-300 mb-4" />
          <p className="font-display font-bold text-xl text-[#2E1065]">No submissions yet</p>
          <p className="text-sm text-slate-500 mt-1">New referrals from the contact form will appear here.</p>
        </div>
      ) : (
        <div className="bg-white rounded-3xl border border-purple-100 shadow-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-submissions-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-purple-100">
                <th className="px-5 py-4">Name</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Home</th>
                <th className="px-5 py-4">Message</th>
                <th className="px-5 py-4">Received</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} className="border-b border-purple-50 hover:bg-[#FAF8F5] transition-colors" data-testid={`admin-row-${r.id}`}>
                  <td className="px-5 py-4 font-bold text-[#2E1065] whitespace-nowrap">{r.full_name}</td>
                  <td className="px-5 py-4 text-slate-600">
                    <div>{r.email}</div>
                    <div className="text-xs text-slate-400">{r.phone}</div>
                  </td>
                  <td className="px-5 py-4 text-slate-600 capitalize whitespace-nowrap">{r.referral_type.replace("_", " ")}</td>
                  <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{r.home_preference.replace(/_/g, " ")}</td>
                  <td className="px-5 py-4 text-slate-600 max-w-[220px] truncate" title={r.message}>{r.message || "—"}</td>
                  <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-4">
                    <select
                      data-testid={`admin-status-select-${r.id}`}
                      value={r.status}
                      onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full border-0 cursor-pointer ${statusStyle[r.status] || statusStyle.new}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>
      )}
    </div>
  );
}
